import type { ReactNode } from "react";
import { Navigate } from "@tanstack/react-router";
import { Box, Spinner } from "@salestrak/ui";
import { useFeatureFlags } from "#app/hooks/use-feature-flags";
import { useWidgetsFlag } from "#app/lib/hooks/use-widgets-flag";

/**
 * The resolved WIDGETS gate for a widget route: `ready` (feature flags loaded)
 * and `enabled` (WIDGETS on). Every widget route MUST wait on `ready` before
 * deciding — flags resolve async, so deciding early bounces flag-on users off
 * the surface on a cold load (U3). Redirect-only routes read this directly; the
 * common render-a-surface case uses {@link WidgetsGate}.
 */
export function useWidgetsGate(): { ready: boolean; enabled: boolean } {
  const { ready } = useFeatureFlags();
  const enabled = useWidgetsFlag();
  return { ready, enabled };
}

/**
 * Wraps a WIDGETS-gated surface: holds on a spinner until flags resolve,
 * redirects to `redirectTo` when the flag is off, else renders `children`. This
 * is the single home for the `ready`→spinner / `!enabled`→`Navigate` block (D1):
 * every widget route, `/my-work` included, gates through it, so no route can
 * skip the `ready` hold (U3).
 */
export function WidgetsGate({
  redirectTo,
  children,
}: {
  redirectTo: string;
  children: ReactNode;
}) {
  const { ready, enabled } = useWidgetsGate();
  if (!ready) {
    return (
      <Box fill="flex" direction="column" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }
  if (!enabled) {
    return <Navigate to={redirectTo as unknown as "/"} replace />;
  }
  return <>{children}</>;
}
